import type { Command } from "../document/commands.ts";
import { COLLISION_CELLS_PER_TILE, type JsonValue, type LayerDocument, type MapDocument, type WorldDocument } from "../document/types.ts";

/**
 * What resolving an intent gives (ADR-086 decision 18), the limits a document grows within (decision 31) and
 * the lookups every resolver shares.
 */

/**
 * The most props a prop layer, or entities an entity layer, takes from an operation.
 */
export const LAYER_ITEMS_MAX = 20_000;

/**
 * The most layers a map takes from an operation.
 */
export const MAP_LAYERS_MAX = 256;

/**
 * The most groups a map takes from an operation.
 */
export const MAP_GROUPS_MAX = 256;

/**
 * The most maps a world takes from an operation.
 */
export const WORLD_MAPS_MAX = 64;

/**
 * The most keys an entity's fields, a behaviour's fields or a map's properties take from an operation.
 */
export const FIELD_KEYS_MAX = 64;

/**
 * The longest JSON an entity's fields, a behaviour's fields or a map's properties take from an operation, in
 * UTF-16 code units. The largest the worlds hold today is a creature spawn's area of 37.5 KB.
 */
export const FIELDS_JSON_MAX = 65_536;

/**
 * The most bytes the grids of a world's layers take together once an operation has added a layer or a map:
 * what keeps a world inside the 32 MiB a flush may write, base64 included.
 */
export const WORLD_GRID_BYTES_MAX = 24 * 1024 * 1024;

/**
 * The most terrain entries a world names from an operation.
 */
export const TERRAIN_NAMES_MAX = 1024;

/**
 * Why an intent was skipped: its target is `gone` (a map, a layer, a group, an item, or the group a layer is
 * to join); the target is of another `kind` (a patch of tiles on a prop layer); a cell is out of `range` of
 * the grid; it names a `tileset` the world does not list; its id is `taken` by something else; a `limit` of
 * decision 31 would be passed; a value is `invalid` in this document (a prop off the tile's pixel lattice);
 * what it removes is still `held` (a group with layers, a tileset in use); it would remove the `last` map; or
 * the document `refused` its commands, which no rule above foresaw, or a program's edit wrote a map's base layer
 * without saying it meant the base (ADR-128 decision 3h, `edits/build.ts`).
 */
export type SkipReason = "gone" | "kind" | "range" | "tileset" | "taken" | "limit" | "invalid" | "held" | "last" | "refused";

/**
 * What became of one intent: it `applied` (at least one key changed), it left everything the `same` (every
 * key already held its value, the item was already there or already gone), or it was `skipped`.
 */
export type IntentOutcome = { readonly status: "applied" } | { readonly status: "same" } | { readonly status: "skipped"; readonly reason: SkipReason };

/**
 * What one intent resolves to against a document: the commands that carry it out, none when it changes
 * nothing, or the reason it is skipped.
 */
export type Resolution = readonly Command[] | SkipReason;

/**
 * A map of a world by id.
 *
 * @param world The document.
 * @param id The map's id.
 * @returns The map, or undefined.
 */
export function mapOf(world: WorldDocument, id: string): MapDocument | undefined {
  return world.maps.find((map) => map.id === id);
}

/**
 * A layer of a world by its map's id and its own.
 *
 * @param world The document.
 * @param mapId The map's id.
 * @param layerId The layer's id.
 * @returns The layer, or undefined when the map or the layer is gone.
 */
export function layerOf(world: WorldDocument, mapId: string, layerId: string): LayerDocument | undefined {
  return mapOf(world, mapId)?.layers.find((layer) => layer.id === layerId);
}

/**
 * Whether a record of fields or properties would pass a growth limit: more than `FIELD_KEYS_MAX` keys or more
 * than `FIELDS_JSON_MAX` units of JSON, and more of either than it holds now. A record a saved document already
 * holds past a limit still takes a change that does not grow it past where it stands.
 *
 * @param before The record as the document holds it, or undefined for a new one.
 * @param after The record the intent would leave.
 * @returns True when the intent must be skipped.
 */
export function passesFieldLimits(before: { readonly [key: string]: JsonValue } | undefined, after: { readonly [key: string]: JsonValue }): boolean {
  const keys = Object.keys(after).length;
  if (keys > FIELD_KEYS_MAX && keys > (before === undefined ? 0 : Object.keys(before).length)) {
    return true;
  }
  const size = JSON.stringify(after).length;
  return size > FIELDS_JSON_MAX && size > (before === undefined ? 0 : JSON.stringify(before).length);
}

/**
 * The bytes a layer's grid takes in a map of a size: a byte a corner, four a tile, a byte a collision, zone or
 * door cell, none for a layer of props or entities.
 *
 * @param kind The layer's kind.
 * @param map The map's size in tiles.
 * @returns The bytes.
 */
export function gridBytes(kind: LayerDocument["kind"], map: { readonly width: number; readonly height: number }): number {
  switch (kind) {
    case "terrain":
      return (map.width + 1) * (map.height + 1);
    case "tile":
      return map.width * map.height * 4;
    case "collision":
    case "zone":
    case "door":
      return map.width * map.height * COLLISION_CELLS_PER_TILE * COLLISION_CELLS_PER_TILE;
    default:
      return 0;
  }
}

/**
 * Whether a world's grids would pass `WORLD_GRID_BYTES_MAX` with more bytes added.
 *
 * @param world The document.
 * @param added The bytes the new layers' grids take; nothing is refused for adding none.
 * @returns True when the add must be skipped.
 */
export function passesGridLimit(world: WorldDocument, added: number): boolean {
  if (added === 0) {
    return false;
  }
  let held = 0;
  for (const map of world.maps) {
    for (const layer of map.layers) {
      held += gridBytes(layer.kind, map);
    }
  }
  return held + added > WORLD_GRID_BYTES_MAX;
}
